import { apiService } from "@/services/api";
import { getErrorMessage, isSuccessStatus } from "@/services/utils/httpUtils";

export default {
  namespaced: true,

  state: {
    ordenes: [],
    checklistActual: [],
    ordenSeleccionada: null,
    solicitudesPendientes: [],
    loading: false,
    error: null,
  },

  getters: {
    ordenes(state) {
      return state.ordenes;
    },

    checklistActual(state) {
      return state.checklistActual;
    },

    ordenSeleccionada(state) {
      return state.ordenSeleccionada;
    },

    solicitudesPendientes(state) {
      return state.solicitudesPendientes;
    },

    loading(state) {
      return state.loading;
    },

    error(state) {
      return state.error;
    },

    getOrdenById(state) {
      return (id) => state.ordenes.find((orden) => orden.id === id);
    },

    // Calcula el progreso del checklist (items recibidos / total)
    progresoChecklist(state) {
      if (state.checklistActual.length === 0) return 0;
      const recibidos = state.checklistActual.filter(
        (item) => item.recibido
      ).length;
      return Math.round((recibidos / state.checklistActual.length) * 100);
    },

    // Total de items en la orden actual
    totalItemsChecklist(state) {
      return state.checklistActual.length;
    },

    // Items recibidos
    itemsRecibidos(state) {
      return state.checklistActual.filter((item) => item.recibido).length;
    },
  },

  mutations: {
    setOrdenes(state, ordenes) {
      state.ordenes = ordenes;
    },

    addOrden(state, orden) {
      state.ordenes.unshift(orden); // Añade al inicio para mostrar la más reciente primero
    },

    setChecklistActual(state, checklist) {
      state.checklistActual = checklist;
    },

    setOrdenSeleccionada(state, orden) {
      state.ordenSeleccionada = orden;
    },

    setSolicitudesPendientes(state, solicitudes) {
      state.solicitudesPendientes = solicitudes;
    },

    removeSolicitudPendiente(state, id) {
      state.solicitudesPendientes = state.solicitudesPendientes.filter(
        (s) => s.id !== id
      );
    },

    updateChecklistItem(state, updatedItem) {
      const index = state.checklistActual.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (index !== -1) {
        state.checklistActual.splice(index, 1, updatedItem);
      }
    },

    setLoading(state, value) {
      state.loading = value;
    },

    setError(state, error) {
      state.error = error;
    },

    clearError(state) {
      state.error = null;
    },

    clearChecklist(state) {
      state.checklistActual = [];
      state.ordenSeleccionada = null;
    },
  },

  actions: {
    /**
     * Obtiene todas las órdenes de compra del tambo actual
     */
    async getOrdenes({ commit }) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.getAll("ordenes_compra");
        if (isSuccessStatus(response.status)) {
          commit("setOrdenes", response.data);
          console.log("✅ Órdenes de compra obtenidas:", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error obteniendo órdenes de compra"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async getSolicitudesPendientes({ commit }) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.getCustom(
          "ordenes_compra/solicitudes/pendientes"
        );
        if (isSuccessStatus(response.status)) {
          commit("setSolicitudesPendientes", response.data);
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error obteniendo solicitudes"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async postSolicitud({ commit }, data) {
      commit("clearError");
      try {
        const response = await apiService.postCustom(
          "ordenes_compra/solicitudes",
          data
        );
        return response;
      } catch (error) {
        const errorMessage = getErrorMessage(error, "Error creando solicitud");
        commit("setError", errorMessage);
        throw error;
      }
    },

    /**
     * Crea solicitudes de compra pendientes a partir de una estructura de orden
     * @param {Object} payload - { ordenData: { fecha, items: Array<{nombre, cantidad, tipo}> } }
     */
    async postSolicitudesDesdeOrden({ commit }, { ordenData }) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.postCustom(
          "ordenes_compra/solicitudes/desde-orden",
          ordenData
        );
        return response;
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error creando solicitudes de compra"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    async patchEstadoSolicitud({ commit }, { id, estado }) {
      commit("clearError");
      try {
        const response = await apiService.patchCustom(
          `ordenes_compra/solicitudes/${id}/estado`,
          { estado }
        );
        if (isSuccessStatus(response.status)) {
          commit("removeSolicitudPendiente", id);
        }
        return response;
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error actualizando solicitud"
        );
        commit("setError", errorMessage);
        throw error;
      }
    },

    /**
     * Crea una nueva orden de compra
     * @param {Object} payload - { items: Array<{nombre, cantidad, precio, tipo}> }
     */
    async postOrden({ commit }, { ordenData }) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.postCustom(
          "ordenes_compra/",
          ordenData
        );
        console.log("🏪 Store recibió response:", response);

        if (isSuccessStatus(response.status)) {
          commit("addOrden", response.data);
          console.log("✅ Orden de compra creada en store:", response.data);
        } else {
          console.warn("⚠️ Status no exitoso:", response.status);
        }

        return response; // SIEMPRE retornar el response
      } catch (error) {
        console.error("❌ Error en postOrden store:", error);
        const errorMessage = getErrorMessage(
          error,
          "Error creando orden de compra"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Obtiene el checklist de una orden específica
     * @param {number} ordenId - ID de la orden
     */
    async getChecklist({ commit }, ordenId) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.getCustom(
          `ordenes_compra/${ordenId}/checklist`
        );
        if (isSuccessStatus(response.status)) {
          commit("setChecklistActual", response.data);
          console.log(
            "✅ Checklist obtenido para orden",
            ordenId,
            response.data
          );
          return response.data;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error obteniendo checklist"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Marca un item del checklist como recibido o no recibido
     * @param {number} itemId - ID del item
     * @param {boolean} recibido - Estado de recibido
     * @param {number} precio - Precio opcional por unidad
     * @param {string} moneda - Moneda opcional
     */
    async marcarRecibido(
      { commit },
      { itemId, recibido, cantidad = null, precio = null, moneda = null }
    ) {
      commit("setLoading", true);
      commit("clearError");
      try {
        let url = `ordenes_compra/checklist/${itemId}/recibido?recibido=${recibido}`;
        if (cantidad !== null && cantidad !== undefined) {
          url += `&cantidad=${cantidad}`;
        }
        if (precio !== null && precio !== undefined) {
          url += `&precio=${precio}`;
        }
        if (moneda !== null && moneda !== undefined) {
          url += `&moneda=${moneda}`;
        }
        const response = await apiService.patchCustom(url);
        if (isSuccessStatus(response.status)) {
          commit("updateChecklistItem", response.data);
          console.log(
            `✅ Item ${itemId} marcado como ${
              recibido ? "recibido" : "no recibido"
            }`
          );
          return response;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error actualizando estado del item"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Elimina una orden de compra
     * @param {number} ordenId - ID de la orden a eliminar
     */
    async deleteOrden({ commit, state }, ordenId) {
      commit("setLoading", true);
      commit("clearError");
      try {
        const response = await apiService.delete("ordenes_compra", ordenId);
        if (isSuccessStatus(response.status)) {
          // Eliminar de la lista local
          const ordenesFiltradas = state.ordenes.filter(
            (orden) => orden.id !== ordenId
          );
          commit("setOrdenes", ordenesFiltradas);
          console.log(`✅ Orden ${ordenId} eliminada correctamente`);
          return response;
        }
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          "Error eliminando orden de compra"
        );
        commit("setError", errorMessage);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * Limpia el checklist actual
     */
    clearChecklist({ commit }) {
      commit("clearChecklist");
    },

    /**
     * Selecciona una orden para ver su checklist
     */
    seleccionarOrden({ commit }, orden) {
      commit("setOrdenSeleccionada", orden);
    },
  },
};
